import { BookingStep } from '../types'
import { cn } from '@w/lib/utils'
import { Check } from 'lucide-react'

interface ProgressIndicatorProps {
  currentStep: BookingStep
}

export default function ProgressIndicator({ currentStep }: ProgressIndicatorProps) {
  const steps = [
    { key: 'service' as BookingStep, number: 1, label: 'Select Service' },
    { key: 'datetime' as BookingStep, number: 2, label: 'Date & Time' },
    { key: 'contact' as BookingStep, number: 3, label: 'Your Info' },
    { key: 'confirm' as BookingStep, number: 4, label: 'Confirm' },
  ]

  const currentIndex = steps.findIndex((s) => s.key === currentStep)

  // Calculate progress line width based on current step
  const getProgressWidth = () => {
    if (currentIndex === 0) return '0%'
    if (currentIndex === 1) return '23%'
    if (currentIndex === 2) return '48%'
    return '73%'
  }

  return (
    <div className="relative flex justify-between items-center max-w-4xl mx-auto px-4">
      {/* Progress Line Background */}
      <div className="absolute top-6 left-[12.5%] right-[12.5%] h-0.5 bg-muted" />

      {/* Progress Line Active */}
      <div
        className="absolute top-6 left-[12.5%] h-0.5 bg-primary transition-all duration-500 ease-in-out"
        style={{
          width: getProgressWidth()
        }}
      />

      {/* Steps */}
      {steps.map((step, index) => {
        const isActive = step.key === currentStep
        const isCompleted = index < currentIndex

        return (
          <div
            key={step.key}
            className="relative flex flex-col items-center flex-1"
          >
            {/* Circle */}
            <div
              className={cn(
                'w-12 h-12 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-300 z-10 shadow-sm',
                isActive && 'bg-primary text-primary-foreground ring-4 ring-primary/20 scale-110',
                isCompleted && 'bg-primary text-primary-foreground',
                !isActive && !isCompleted && 'bg-background text-muted-foreground border-2 border-muted'
              )}
            >
              {isCompleted ? (
                <Check className="w-5 h-5" />
              ) : (
                step.number
              )}
            </div>

            {/* Label */}
            <div
              className={cn(
                'mt-3 text-sm text-center font-medium transition-colors duration-300',
                isActive && 'text-foreground font-semibold',
                isCompleted && 'text-foreground',
                !isActive && !isCompleted && 'text-muted-foreground'
              )}
            >
              {step.label}
            </div>
          </div>
        )
      })}
    </div>
  )
}
